import axios from 'axios';
import { combineEpics } from 'redux-observable';
import { filter, mergeMap } from 'rxjs/operators';

import { AuthorizationConflictResponse, AuthorizationStatus } from '../../../domain/authorization';
import { RootEpic } from '../../../store/epics';
import { isEmbedded } from '../../../utils';
import {
  authorizeWithOtp,
  authorizeWithOtpConflict,
  authorizeWithOtpFail,
  authorizeWithOtpPhoneInUse,
  authorizeWithOtpSuccess,
  requestOtp,
  requestVerification,
  requestVerificationFail,
  requestVerificationSuccess,
  resendOtp,
  resendVerification,
  verifyOtp,
} from '../reducer/session';

const requestVerificationEpic: RootEpic = (action$, state$, { api }) => {
  return action$.pipe(
    filter(requestVerification.match),
    mergeMap(({ payload }) => {
      if (!payload.embeddedSessionToken && isEmbedded()) {
        return [requestVerificationFail({ message: 'Missing session token' })];
      }

      const request = payload.embeddedSessionToken
        ? api.authorization.requestEmbeddedVerification(payload)
        : api.authorization.requestVerification(payload);
      return request.then(() => requestVerificationSuccess()).catch((err: Error) => requestVerificationFail(err));
    }),
  );
};

const requestOtpEpic: RootEpic = (action$, state$, { api }) => {
  return action$.pipe(
    filter(requestOtp.match),
    mergeMap(({ payload }) =>
      api.authorization
        .requestOtp(payload)
        .then(() => requestVerificationSuccess())
        .catch((err: Error) => requestVerificationFail(err)),
    ),
  );
};

const resendVerificationEpic: RootEpic = (action$, _state$, { api }) => {
  return action$.pipe(
    filter(resendVerification.match),
    mergeMap(() =>
      api.authorization
        .resendVerification()
        .then(() => requestVerificationSuccess())
        .catch((err: Error) => requestVerificationFail(err)),
    ),
  );
};

const resendOtpEpic: RootEpic = (action$, _state$, { api }) => {
  return action$.pipe(
    filter(resendOtp.match),
    mergeMap(({ payload }) =>
      api.authorization
        .resendOtp({ channel: payload })
        .then(() => requestVerificationSuccess())
        .catch((err: Error) => requestVerificationFail(err)),
    ),
  );
};

const actionByAuthorizationStatus: Record<AuthorizationStatus, { payload: undefined; type: string }> = {
  ok: authorizeWithOtpSuccess(),
  existing_user: authorizeWithOtpPhoneInUse(),
};

const authorizeWithOtpEpic: RootEpic = (action$, state$, { api }) => {
  return action$.pipe(
    filter(authorizeWithOtp.match),
    mergeMap(({ payload }) =>
      api.authorization
        .requestAuthorization({ otp: payload })
        .then((resp) => actionByAuthorizationStatus[resp.authorizationStatus])
        .catch((err: Error) => {
          if (axios.isAxiosError(err) && err.response?.status === 409 && err.response.data) {
            return authorizeWithOtpConflict(err.response.data as AuthorizationConflictResponse);
          } else {
            return authorizeWithOtpFail(err);
          }
        }),
    ),
  );
};

const verifyOtpEpic: RootEpic = (action$, state$, { api }) => {
  return action$.pipe(
    filter(verifyOtp.match),
    mergeMap(({ payload }) =>
      api.authorization
        .verifyOtp({ otp: payload })
        .then(() => authorizeWithOtpSuccess())
        .catch((err: Error) => {
          if (axios.isAxiosError(err) && err.response?.status === 409 && err.response.data) {
            return authorizeWithOtpConflict(err.response.data as AuthorizationConflictResponse);
          } else {
            return authorizeWithOtpFail(err);
          }
        }),
    ),
  );
};

export const sessionEpic = combineEpics(
  authorizeWithOtpEpic,
  requestOtpEpic,
  requestVerificationEpic,
  resendOtpEpic,
  resendVerificationEpic,
  verifyOtpEpic,
);
